import { useState, useEffect, useRef } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { IndianRupee, Plus, FileText, Check, X, Download, Printer, Upload, Search, Trash2, Package, FileSpreadsheet, Loader2, Zap, MessageCircle, Percent, Tag, ShoppingCart, Store, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { openInvoicePrint, downloadInvoicePdf, shareInvoiceWhatsApp } from '../components/InvoiceTemplate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prices, setPrices] = useState([]);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [priceSearch, setPriceSearch] = useState('');
  const [itemSuggestions, setItemSuggestions] = useState({});
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [pendingQueueId, setPendingQueueId] = useState(null);
  const fileInputRef = useRef(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [priceForm, setPriceForm] = useState({ name: '', category: 'medicine', price: '', unit: '', current_stock: '', min_stock: '', batch_number: '', batch_expiry: '', notes: '' });
  const [form, setForm] = useState({
    patient_id: '', patient_name: '',
    items: [{ description: 'Consultation Fee', amount: '', quantity: 1 }],
    gst_percent: '0', payment_method: 'cash', notes: '',
    discount_type: 'none', discount_value: 0,
    patient_address: '', sample_collection_time: ''
  });
  const [reportDates, setReportDates] = useState({ start: '', end: '' });
  
  // Invoice search state
  const [invoiceSearch, setInvoiceSearch] = useState('');
  
  // Marketplace product search state
  const [marketplaceSearch, setMarketplaceSearch] = useState('');
  const [marketplaceProducts, setMarketplaceProducts] = useState([]);
  const [searchingMarketplace, setSearchingMarketplace] = useState(false);
  const [showMarketplaceDropdown, setShowMarketplaceDropdown] = useState(false);
  const marketplaceSearchRef = useRef(null);
  const marketplaceDropdownRef = useRef(null);
  const seenQueueIds = useRef(new Set());
  const firstQueueLoadDone = useRef(false);

  // Soft chime (Web Audio, no asset needed) - plays when a new patient lands in the Reception Queue
  const playQueueChime = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch (e) { /* audio not available, ignore */ }
  };

  useEffect(() => {
    const loadAll = () => Promise.all([
      API.get('/billing'), API.get('/patients'), API.get('/billing/summary'), API.get('/prices'), API.get('/clinic-settings'), API.get('/billing/reception-queue/pending').catch(() => ({ data: [] }))
    ]).then(([inv, pat, sum, pr, cs, pq]) => {
      setInvoices(inv.data); setPatients(pat.data); setSummary(sum.data); setPrices(pr.data); setClinicSettings(cs.data);
      const queueData = pq.data || [];
      const hasNewPatient = firstQueueLoadDone.current && queueData.some(e => !seenQueueIds.current.has(e.id));
      queueData.forEach(e => seenQueueIds.current.add(e.id));
      firstQueueLoadDone.current = true;
      if (hasNewPatient) playQueueChime();
      setPendingQueue(queueData);
      // Auto-fill consultation fee from clinic settings
      const fee = cs.data?.consultation_fee;
      if (fee) {
        setForm(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.description === 'Consultation Fee' && !item.amount ? { ...item, amount: fee } : item
          )
        }));
      }
    }).catch(console.error);

    loadAll().finally(() => setLoading(false));

    const interval = setInterval(() => { if (!document.hidden) loadAll(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close marketplace dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        marketplaceDropdownRef.current && 
        !marketplaceDropdownRef.current.contains(event.target) &&
        marketplaceSearchRef.current &&
        !marketplaceSearchRef.current.contains(event.target)
      ) {
        setShowMarketplaceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search marketplace products
  const searchMarketplaceProducts = async (query) => {
    if (!query || query.length < 2) {
      setMarketplaceProducts([]);
      setShowMarketplaceDropdown(false);
      return;
    }
    setSearchingMarketplace(true);
    try {
      const res = await API.get(`/billing/marketplace-products?search=${encodeURIComponent(query)}`);
      setMarketplaceProducts(res.data.products || []);
      setShowMarketplaceDropdown(true);
    } catch (err) {
      console.error('Marketplace search error:', err);
      setMarketplaceProducts([]);
    } finally {
      setSearchingMarketplace(false);
    }
  };

  // Debounced marketplace search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (marketplaceSearch.length >= 2) {
        searchMarketplaceProducts(marketplaceSearch);
      } else {
        setMarketplaceProducts([]);
        setShowMarketplaceDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [marketplaceSearch]);

  // Add marketplace product to invoice items
  const addMarketplaceProduct = (product) => {
    const newItem = {
      description: product.name,
      amount: String(product.mrp || product.price || 0),
      quantity: 1,
      product_id: product.id,
      product_type: product.type,
      vendor_id: product.vendor_id,
      vendor_name: product.vendor_name,
      b2b_price: product.price || 0
    };
    setForm({ ...form, items: [...form.items, newItem] });
    setMarketplaceSearch('');
    setShowMarketplaceDropdown(false);
    toast.success(`Added ${product.name} from ${product.vendor_name}`);
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', amount: '', quantity: 1 }] });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });

    if (field === 'description' && value.length >= 2) {
      const matches = prices.filter(p => p.name.toLowerCase().includes(value.toLowerCase())).slice(0, 5);
      setItemSuggestions({ ...itemSuggestions, [i]: matches });
    } else if (field === 'description') {
      setItemSuggestions({ ...itemSuggestions, [i]: [] });
    }
  };
  const selectSuggestion = (itemIndex, price) => {
    const items = [...form.items];
    items[itemIndex] = { description: price.name, amount: String(price.price) };
    setForm({ ...form, items });
    setItemSuggestions({ ...itemSuggestions, [itemIndex]: [] });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });
  const subtotal = form.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0) * (parseInt(item.quantity) || 1), 0);
  // Calculate discount
  const discountAmount = form.discount_type === 'percentage' 
    ? subtotal * (parseFloat(form.discount_value) || 0) / 100 
    : form.discount_type === 'amount' 
    ? Math.min(parseFloat(form.discount_value) || 0, subtotal) 
    : 0;
  const afterDiscount = subtotal - discountAmount;
  const gstAmount = afterDiscount * (parseFloat(form.gst_percent) || 0) / 100;
  const total = afterDiscount + gstAmount;

  // Auto-populate from clinical visit
  const handleAutoPopulate = async () => {
    if (!form.patient_id) { toast.error('Select a patient first'); return; }
    setAutoPopulating(true);
    try {
      const res = await API.get(`/clinical/invoice-items/${form.patient_id}`);
      if (res.data.items.length === 0) {
        toast.info('No billable items found from recent visit');
        return;
      }
      const newItems = res.data.items.map(item => ({
        description: item.description,
        amount: String(item.amount || ''),
        quantity: 1,
        product_id: item.product_id || null,
        product_type: item.product_type || null,
        vendor_id: item.vendor_id || null,
        vendor_name: item.vendor_name || null,
        b2b_price: item.b2b_price || null
      }));
      setForm({ ...form, items: newItems });
      const marketplaceCount = newItems.filter(i => i.vendor_id).length;
      toast.success(`Imported ${newItems.length} items${marketplaceCount > 0 ? ` (${marketplaceCount} from marketplace)` : ''}`);
    } catch (err) { toast.error('Failed to fetch items'); }
    finally { setAutoPopulating(false); }
  };

  const handleGenerateFromQueue = (entry) => {
    setForm({
      patient_id: entry.patient_id, patient_name: entry.patient_name,
      items: [{ description: 'Consultation Fee', amount: entry.consultation_fee, quantity: 1 }],
      gst_percent: '0', payment_method: 'cash', notes: '',
      discount_type: 'none', discount_value: 0, patient_address: '', sample_collection_time: ''
    });
    setPendingQueueId(entry.id);
    setDialogOpen(true);
  };

  const handleDismissQueue = async (entry) => {
    try {
      await API.delete(`/billing/reception-queue/${entry.id}?status=dismissed`);
      setPendingQueue(prev => prev.filter(e => e.id !== entry.id));
      toast.success(`${entry.patient_name} removed from queue`);
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const invoiceItems = form.items
        .filter(i => i.description && i.amount)
        .map(i => ({
          description: i.description,
          amount: parseFloat(i.amount),
          quantity: parseInt(i.quantity) || 1,
          product_id: i.product_id || null,
          product_type: i.product_type || null,
          vendor_id: i.vendor_id || null,
          vendor_name: i.vendor_name || null,
          b2b_price: i.b2b_price ? parseFloat(i.b2b_price) : null
        }));
      
      await API.post('/billing', {
        patient_id: form.patient_id, patient_name: form.patient_name,
        items: invoiceItems,
        gst_percent: parseFloat(form.gst_percent) || 0, payment_method: form.payment_method, notes: form.notes,
        discount_type: form.discount_type, discount_value: parseFloat(form.discount_value) || 0,
        patient_address: form.patient_address || null,
        sample_collection_time: form.sample_collection_time || null,
      });
      if (pendingQueueId) {
        await API.delete(`/billing/reception-queue/${pendingQueueId}`).catch(() => {});
        setPendingQueueId(null);
        API.get('/billing/reception-queue/pending').then(r => setPendingQueue(r.data || [])).catch(() => {});
      }
      toast.success('Invoice created');
      setDialogOpen(false);
      setForm({ patient_id: '', patient_name: '', items: [{ description: 'Consultation Fee', amount: '', quantity: 1 }], gst_percent: '0', payment_method: 'cash', notes: '', discount_type: 'none', discount_value: 0, patient_address: '', sample_collection_time: '' });
      const [inv, sum] = await Promise.all([API.get('/billing'), API.get('/billing/summary')]);
      setInvoices(inv.data); setSummary(sum.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const markPaid = async (id) => {
    try {
      await API.put(`/billing/${id}`, { payment_status: 'paid' });
      toast.success('Marked as paid');
      const [inv, sum] = await Promise.all([API.get('/billing'), API.get('/billing/summary')]);
      setInvoices(inv.data); setSummary(sum.data);
    } catch (err) { toast.error('Update failed'); }
  };

  const downloadInvoice = async (inv) => {
    downloadInvoicePdf(inv, clinicSettings);
  };

  const printInvoice = async (inv) => {
    openInvoicePrint(inv, clinicSettings);
  };

  const downloadCatalogTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prices/template/download`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'price_catalog_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const shareInvoiceViaWhatsApp = (inv) => {
    // Find patient phone from the patients list
    const patient = patients.find(p => p.id === inv.patient_id);
    shareInvoiceWhatsApp(inv, clinicSettings, patient?.phone || '');
  };

  // Download Excel Reports
  const downloadRevenueExcel = async () => {
    setDownloadingReport(true);
    try {
      const params = new URLSearchParams();
      if (reportDates.start) params.append('start_date', reportDates.start);
      if (reportDates.end) params.append('end_date', reportDates.end);
      
      const response = await fetch(`${BACKEND_URL}/api/reports/revenue-excel?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Revenue report downloaded');
    } catch (err) { toast.error('Download failed'); }
    finally { setDownloadingReport(false); }
  };

  const downloadPatientsExcel = async () => {
    setDownloadingReport(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/patients-excel`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patients_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Patient list downloaded');
    } catch (err) { toast.error('Download failed'); }
    finally { setDownloadingReport(false); }
  };

  const handleAddPrice = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...priceForm,
        price: parseFloat(priceForm.price) || 0,
        current_stock: priceForm.current_stock !== '' ? parseInt(priceForm.current_stock) : null,
        min_stock: priceForm.min_stock !== '' ? parseInt(priceForm.min_stock) : null,
        batch_number: priceForm.batch_number || null,
        batch_expiry: priceForm.batch_expiry || null,
        notes: priceForm.notes || null
      };
      if (editingPrice) {
        await API.put(`/prices/${editingPrice.id}`, payload);
        toast.success('Item updated');
      } else {
        await API.post('/prices', payload);
        toast.success('Price added');
      }
      resetPriceForm();
      const pr = await API.get('/prices');
      setPrices(pr.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const resetPriceForm = () => {
    setPriceForm({ name: '', category: 'medicine', price: '', unit: '', current_stock: '', min_stock: '', batch_number: '', batch_expiry: '', notes: '' });
    setEditingPrice(null);
    setPriceDialogOpen(false);
  };

  const openEditPrice = (p) => {
    setEditingPrice(p);
    setPriceForm({
      name: p.name || '',
      category: p.category || 'medicine',
      price: p.price?.toString() || '',
      unit: p.unit || '',
      current_stock: p.current_stock != null ? p.current_stock.toString() : '',
      min_stock: p.min_stock != null ? p.min_stock.toString() : '',
      batch_number: p.batch_number || '',
      batch_expiry: p.batch_expiry || '',
      notes: p.notes || ''
    });
    setPriceDialogOpen(true);
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await API.post('/prices/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      const pr = await API.get('/prices');
      setPrices(pr.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };

  const deletePrice = async (id) => {
    try { await API.delete(`/prices/${id}`); const pr = await API.get('/prices'); setPrices(pr.data); toast.success('Deleted'); }
    catch (err) { toast.error('Delete failed'); }
  };

  const filteredPrices = prices.filter(p => !priceSearch || p.name.toLowerCase().includes(priceSearch.toLowerCase()));

  const statusColors = { pending: 'bg-amber-100 text-amber-800', paid: 'bg-emerald-100 text-emerald-800', partial: 'bg-blue-100 text-blue-800' };

  return (
    <div data-testid="billing-page" className="space-y-6">
      {pendingQueue.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 sm:p-4" data-testid="pending-unbilled-ribbon">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Pending Unbilled Patients ({pendingQueue.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {pendingQueue.map(entry => (
              <div key={entry.id} className="flex items-center gap-3 bg-white dark:bg-slate-900 border border-amber-200 rounded-lg px-3 py-2" data-testid={`pending-queue-item-${entry.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">{entry.patient_name}</p>
                  <p className="text-xs text-slate-500">₹{entry.consultation_fee} • sent from cabin</p>
                </div>
                <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800 text-white" onClick={() => handleGenerateFromQueue(entry)} data-testid={`generate-invoice-queue-${entry.id}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Generate Invoice
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => handleDismissQueue(entry)} data-testid={`dismiss-queue-${entry.id}`} title="Dismiss (sent by mistake)">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>Billing</h1>
          <p className="text-sm text-slate-500">Manage invoices, prices, and payments</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setPendingQueueId(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white h-11" data-testid="billing-add-btn">
                <Plus className="w-4 h-4 mr-2" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Create Invoice</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>Patient</Label>
                  <Select value={form.patient_id} onValueChange={v => { const p = patients.find(p => p.id === v); setForm({...form, patient_id: v, patient_name: p?.name || ''}); }}>
                    <SelectTrigger className="h-12 mt-1" data-testid="billing-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>{patients.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                
                {/* Auto-populate button */}
                <Button type="button" variant="outline" className="w-full h-10 text-teal-700 border-teal-200 hover:bg-teal-50" onClick={handleAutoPopulate} disabled={autoPopulating || !form.patient_id} data-testid="auto-populate-btn">
                  {autoPopulating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  Auto-populate from Clinical Visit
                </Button>
                
                <div className="space-y-2">
                  <Label>Items (type to auto-suggest from price catalog)</Label>
                  {form.items.map((item, i) => (
                    <div key={`el-${i}`} className="relative">
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Input data-testid={`invoice-item-desc-${i}`} value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Description" className="h-10" />
                          {item.vendor_name && (
                            <Badge variant="secondary" className="mt-1 text-[10px] bg-purple-100 text-purple-700">
                              <Store className="w-3 h-3 mr-1" />{item.vendor_name}
                            </Badge>
                          )}
                        </div>
                        <div className="w-16">
                          <Input data-testid={`invoice-item-qty-${i}`} type="number" min="1" value={item.quantity || 1} onChange={e => updateItem(i, 'quantity', e.target.value)} placeholder="Qty" className="h-10" />
                        </div>
                        <div className="w-24">
                          <Input data-testid={`invoice-item-amount-${i}`} type="number" value={item.amount} onChange={e => updateItem(i, 'amount', e.target.value)} placeholder={'₹'} className="h-10" />
                        </div>
                        {form.items.length > 1 && <Button type="button" variant="ghost" size="sm" className="h-10 text-red-500" onClick={() => removeItem(i)}><X className="w-4 h-4" /></Button>}
                      </div>
                      {itemSuggestions[i]?.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {itemSuggestions[i].map(p => (
                            <button key={p.id} type="button" onClick={() => selectSuggestion(i, p)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex justify-between items-center" data-testid={`suggestion-${p.id}`}>
                              <span>{p.name} <span className="text-xs text-slate-400">({p.category})</span></span>
                              <span className="font-medium">{'₹'}{p.price}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="invoice-add-item-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
                  </div>
                </div>
                
                {/* Marketplace Product Search */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-purple-700 flex items-center gap-2 mb-2"><ShoppingCart className="w-4 h-4" /> Add from Marketplace</Label>
                  <div className="relative" ref={marketplaceSearchRef}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      data-testid="marketplace-product-search"
                      value={marketplaceSearch}
                      onChange={e => setMarketplaceSearch(e.target.value)}
                      onFocus={() => marketplaceProducts.length > 0 && setShowMarketplaceDropdown(true)}
                      placeholder="Search medicines, lab tests, equipment from vendors..."
                      className="h-10 pl-10"
                    />
                    {searchingMarketplace && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-purple-500" />
                    )}
                    {showMarketplaceDropdown && marketplaceProducts.length > 0 && (
                      <div ref={marketplaceDropdownRef} className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-purple-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {marketplaceProducts.map(p => (
                          <button 
                            key={p.id} 
                            type="button" 
                            onClick={() => addMarketplaceProduct(p)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                            data-testid={`marketplace-product-${p.id}`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-medium text-slate-900 dark:text-white">{p.name}</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <Badge variant="secondary" className="text-[10px]">
                                    {p.type === 'medicine' ? 'Medicine' : p.type === 'lab_test' ? 'Lab Test' : 'Equipment'}
                                  </Badge>
                                  <span className="text-xs text-purple-600"><Store className="w-3 h-3 inline mr-1" />{p.vendor_name}</span>
                                </div>
                                {p.type === 'lab_test' && p.turnaround_time && (
                                  <p className="text-[10px] text-slate-400 mt-0.5">TAT: {p.turnaround_time}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-purple-700">₹{p.price}</span>
                                {p.mrp > p.price && (
                                  <p className="text-[10px] text-slate-400 line-through">MRP ₹{p.mrp}</p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showMarketplaceDropdown && marketplaceProducts.length === 0 && marketplaceSearch.length >= 2 && !searchingMarketplace && (
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-purple-200 rounded-lg shadow-lg p-3 text-center text-sm text-slate-500">
                        No products found from your connected vendors
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-purple-600 mt-2">Products from approved vendors only. Vendors will be notified.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>GST %</Label>
                    <Select value={form.gst_percent} onValueChange={v => setForm({...form, gst_percent: v})}>
                      <SelectTrigger className="h-12 mt-1" data-testid="billing-gst-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="0">No GST</SelectItem><SelectItem value="5">5%</SelectItem><SelectItem value="12">12%</SelectItem><SelectItem value="18">18%</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payment Method</Label>
                    <Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}>
                      <SelectTrigger className="h-12 mt-1" data-testid="billing-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Discount Section */}
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-green-700 flex items-center gap-2 mb-2"><Tag className="w-4 h-4" /> Discount</Label>
                  <div className="flex gap-2">
                    <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v, discount_value: 0})}>
                      <SelectTrigger className="w-36" data-testid="billing-discount-type"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Discount</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="amount">Fixed Amount (₹)</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.discount_type !== 'none' && (
                      <Input 
                        type="number" 
                        placeholder={form.discount_type === 'percentage' ? '% off' : '₹ amount'} 
                        value={form.discount_value || ''} 
                        onChange={e => setForm({...form, discount_value: e.target.value})}
                        className="w-28"
                        data-testid="billing-discount-value"
                      />
                    )}
                    {form.discount_type !== 'none' && discountAmount > 0 && (
                      <span className="flex items-center text-sm text-green-600 font-medium">-₹{discountAmount.toFixed(2)}</span>
                    )}
                  </div>
                </div>

                {/* Patient Address & Collection Time (shown when lab tests are in items) */}
                {form.items.some(i => i.product_type === 'lab_test') && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                    <Label className="text-purple-700 font-medium">Lab Sample Collection Details</Label>
                    <div>
                      <Label className="text-sm">Patient Address</Label>
                      <Input
                        placeholder="Enter patient's address for sample collection"
                        value={form.patient_address || ''}
                        onChange={e => setForm({...form, patient_address: e.target.value})}
                        className="mt-1"
                        data-testid="billing-patient-address"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Sample Collection Time</Label>
                      <Input
                        type="datetime-local"
                        value={form.sample_collection_time || ''}
                        onChange={e => setForm({...form, sample_collection_time: e.target.value})}
                        className="mt-1"
                        data-testid="billing-collection-time"
                      />
                    </div>
                  </div>
                )}

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>{'₹'}{subtotal.toFixed(2)}</span></div>
                  {discountAmount > 0 && <div className="flex justify-between text-sm text-green-600"><span>Discount ({form.discount_type === 'percentage' ? `${form.discount_value}%` : `₹${form.discount_value}`})</span><span>-₹{discountAmount.toFixed(2)}</span></div>}
                  {parseFloat(form.gst_percent) > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">GST ({form.gst_percent}%)</span><span>{'₹'}{gstAmount.toFixed(2)}</span></div>}
                  <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-1 mt-1"><span>Total</span><span>{'₹'}{total.toFixed(2)}</span></div>
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="invoice-submit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : 'Create Invoice'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200 dark:border-slate-800"><CardContent className="p-5"><p className="text-xs text-slate-500 mb-1">Today's Revenue</p><p className="text-2xl font-bold" data-testid="billing-daily-revenue">{'₹'}{(summary?.daily?.total || 0).toLocaleString('en-IN')}</p><p className="text-xs text-slate-400">{summary?.daily?.count || 0} invoices</p></CardContent></Card>
        <Card className="border-slate-200 dark:border-slate-800"><CardContent className="p-5"><p className="text-xs text-slate-500 mb-1">Monthly Revenue</p><p className="text-2xl font-bold" data-testid="billing-monthly-revenue">{'₹'}{(summary?.monthly?.total || 0).toLocaleString('en-IN')}</p><p className="text-xs text-slate-400">{summary?.monthly?.count || 0} invoices</p></CardContent></Card>
        <Card className="border-slate-200 dark:border-slate-800"><CardContent className="p-5"><p className="text-xs text-slate-500 mb-1">Pending</p><p className="text-2xl font-bold text-amber-600" data-testid="billing-pending-count">{invoices.filter(i => i.payment_status === 'pending').length}</p><p className="text-xs text-slate-400">unpaid invoices</p></CardContent></Card>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices"><FileText className="w-4 h-4 mr-2" /> Invoices</TabsTrigger>
          <TabsTrigger value="prices" data-testid="billing-tab-prices"><Package className="w-4 h-4 mr-2" /> Catalog</TabsTrigger>
          <TabsTrigger value="reports" data-testid="billing-tab-reports"><FileSpreadsheet className="w-4 h-4 mr-2" /> Reports</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Recent Invoices</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search by patient, invoice #..."
                    value={invoiceSearch}
                    onChange={e => setInvoiceSearch(e.target.value)}
                    className="pl-10 w-full sm:w-[250px]"
                    data-testid="invoice-search"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="billing-invoices-list">
              {loading ? <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-teal-700 border-t-transparent rounded-full animate-spin" /></div> :
               invoices.filter(inv => {
                 if (!invoiceSearch) return true;
                 const search = invoiceSearch.toLowerCase();
                 return inv.patient_name?.toLowerCase().includes(search) ||
                        inv.invoice_number?.toLowerCase().includes(search) ||
                        inv.date?.includes(search);
               }).length === 0 ? <div className="text-center py-12"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-400 text-sm">{invoiceSearch ? 'No invoices match your search' : 'No invoices yet'}</p></div> :
               invoices.filter(inv => {
                 if (!invoiceSearch) return true;
                 const search = invoiceSearch.toLowerCase();
                 return inv.patient_name?.toLowerCase().includes(search) ||
                        inv.invoice_number?.toLowerCase().includes(search) ||
                        inv.date?.includes(search);
               }).map((inv, i) => (
                <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-800 hover:shadow-sm transition-all" data-testid={`invoice-item-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-slate-500" /></div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{inv.patient_name}</p>
                      <p className="text-xs text-slate-400 truncate">{inv.invoice_number} | {inv.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <span className="font-bold text-sm">{'₹'}{inv.total?.toLocaleString('en-IN')}</span>
                    <Badge className={`text-[10px] ${statusColors[inv.payment_status]}`}>{inv.payment_status}</Badge>
                    <Button size="sm" variant="ghost" className="h-8 text-green-600" onClick={() => shareInvoiceViaWhatsApp(inv)} data-testid={`invoice-whatsapp-${i}`} title="Send to WhatsApp">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-teal-600" onClick={() => downloadInvoice(inv)} data-testid={`invoice-download-${i}`} title="Download PDF">
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => printInvoice(inv)} data-testid={`invoice-print-${i}`} title="Print">
                      <Printer className="w-4 h-4" />
                    </Button>
                    {inv.payment_status === 'pending' && (
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => markPaid(inv.id)} data-testid={`invoice-pay-${i}`}>
                        <Check className="w-3 h-3 mr-1" /> Paid
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Catalog Tab */}
        <TabsContent value="prices">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Medicine & Test Price Catalog</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={downloadCatalogTemplate} data-testid="price-template-btn">
                    <Download className="w-4 h-4 mr-1" /> Template
                  </Button>
                  <input type="file" ref={fileInputRef} accept=".csv,.xlsx,.xls" onChange={handleExcelUpload} className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="price-upload-btn">
                    <Upload className="w-4 h-4 mr-1" /> Upload
                  </Button>
                  <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="price-add-btn" onClick={() => { resetPriceForm(); setPriceDialogOpen(true); }}><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="price-search-input" value={priceSearch} onChange={e => setPriceSearch(e.target.value)} placeholder="Search medicines, tests, treatments..." className="h-11 pl-10" />
              </div>
              <p className="text-xs text-slate-400 mb-3">Upload format: CSV/Excel with columns: name, price, category, unit, current_stock, min_stock, batch_number, batch_expiry, notes</p>
              <div className="space-y-1" data-testid="price-catalog-list">
                {filteredPrices.length === 0 ? (
                  <div className="text-center py-8"><Package className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">No prices in catalog. Add manually or upload Excel.</p></div>
                ) : filteredPrices.map((p, i) => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg" data-testid={`price-item-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{p.name}</span>
                        {p.unit && <span className="text-xs text-slate-400">{p.unit}</span>}
                        <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>
                      </div>
                      {p.category === 'medicine' && p.current_stock != null && (
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span>Stock: <span className={p.current_stock <= (p.min_stock || 0) ? 'text-red-500 font-medium' : 'text-emerald-600 font-medium'}>{p.current_stock}</span></span>
                          {p.min_stock != null && <span>Min: {p.min_stock}</span>}
                          {p.batch_number && <span>Batch: {p.batch_number}</span>}
                          {p.batch_expiry && <span>Exp: {p.batch_expiry}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="font-bold text-sm whitespace-nowrap">{'₹'}{p.price}</span>
                      <Button variant="ghost" size="sm" className="h-8 text-teal-600 hover:text-teal-800" onClick={() => openEditPrice(p)} data-testid={`price-edit-${i}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="h-8 text-red-400 hover:text-red-600" onClick={() => deletePrice(p.id)} data-testid={`price-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Add/Edit Price Dialog */}
          <Dialog open={priceDialogOpen} onOpenChange={(open) => { if (!open) resetPriceForm(); else setPriceDialogOpen(true); }}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>{editingPrice ? 'Edit Catalog Item' : 'Add to Catalog'}</DialogTitle></DialogHeader>
              <form onSubmit={handleAddPrice} className="space-y-4">
                <div><Label>Name *</Label><Input data-testid="price-name-input" value={priceForm.name} onChange={e => setPriceForm({...priceForm, name: e.target.value})} className="h-11 mt-1" placeholder="Paracetamol 500mg" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={priceForm.category} onValueChange={v => setPriceForm({...priceForm, category: v})}>
                      <SelectTrigger className="h-11 mt-1" data-testid="price-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medicine">Medicine</SelectItem>
                        <SelectItem value="test">Test/Lab</SelectItem>
                        <SelectItem value="radiology">Radiology</SelectItem>
                        <SelectItem value="procedure">Procedure/Treatment</SelectItem>
                        <SelectItem value="consultation">Consultation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Price ({'₹'}) *</Label><Input data-testid="price-amount-input" type="number" value={priceForm.price} onChange={e => setPriceForm({...priceForm, price: e.target.value})} className="h-11 mt-1" placeholder="100" required /></div>
                </div>
                <div><Label>Unit (optional)</Label><Input value={priceForm.unit} onChange={e => setPriceForm({...priceForm, unit: e.target.value})} className="h-11 mt-1" placeholder="per tablet, per test, etc." /></div>
                
                {priceForm.category === 'medicine' && (
                  <div className="border-t pt-4 space-y-4">
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Medicine Stock Details (auto-syncs to Inventory)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Current Stock</Label><Input type="number" value={priceForm.current_stock} onChange={e => setPriceForm({...priceForm, current_stock: e.target.value})} className="h-11 mt-1" placeholder="0" data-testid="price-stock-input" /></div>
                      <div><Label>Min Stock (Alert)</Label><Input type="number" value={priceForm.min_stock} onChange={e => setPriceForm({...priceForm, min_stock: e.target.value})} className="h-11 mt-1" placeholder="0" data-testid="price-min-stock-input" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Batch Number</Label><Input value={priceForm.batch_number} onChange={e => setPriceForm({...priceForm, batch_number: e.target.value})} className="h-11 mt-1" placeholder="e.g., BTH-2024-001" data-testid="price-batch-input" /></div>
                      <div><Label>Batch Expiry</Label><Input type="date" value={priceForm.batch_expiry} onChange={e => setPriceForm({...priceForm, batch_expiry: e.target.value})} className="h-11 mt-1" data-testid="price-expiry-input" /></div>
                    </div>
                    <div><Label>Notes</Label><Textarea value={priceForm.notes} onChange={e => setPriceForm({...priceForm, notes: e.target.value})} className="mt-1" placeholder="Additional notes..." rows={2} data-testid="price-notes-input" /></div>
                  </div>
                )}
                <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="price-submit-btn">{editingPrice ? 'Save Changes' : 'Add to Catalog'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Download Excel Reports</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Revenue Report */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-teal-600" /> Revenue Report
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input type="date" value={reportDates.start} onChange={e => setReportDates({...reportDates, start: e.target.value})} className="h-10 mt-1" data-testid="report-start-date" />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input type="date" value={reportDates.end} onChange={e => setReportDates({...reportDates, end: e.target.value})} className="h-10 mt-1" data-testid="report-end-date" />
                  </div>
                </div>
                <Button onClick={downloadRevenueExcel} disabled={downloadingReport} className="w-full bg-teal-700 hover:bg-teal-800 text-white" data-testid="download-revenue-btn">
                  {downloadingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  Download Revenue Excel
                </Button>
                <p className="text-xs text-slate-400 mt-2">Leave dates empty to export all invoices</p>
              </div>

              {/* Patient List Report */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" /> Patient List Export
                </h3>
                <Button onClick={downloadPatientsExcel} disabled={downloadingReport} variant="outline" className="w-full" data-testid="download-patients-btn">
                  {downloadingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download Patient List Excel
                </Button>
                <p className="text-xs text-slate-400 mt-2">Exports all patient records with contact info</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
